import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../../lib/tokens';
import {
  fetchUserTutorSessions,
  fetchUserTutorSessionTranscript,
  type TutorSessionListItem,
} from './api';
import { TutorMemorySection } from './TutorMemorySection';
import { formatDate, formatRelative } from './format';

interface Props {
  userId: string;
  app: string;
  enabled: boolean;
}

// Read-only Einsicht in die Text-Tutor-Chats eines Users (Spec
// tutor-text-session-sync.md, B3). Lazy: lädt erst, wenn der Tab aktiv ist.
// Links die Session-Liste, rechts das ausgewählte Transkript (User/Assistant-
// Bubbles). Chat-Inhalte sind personenbezogen → nur für ADMINs sichtbar.
// `app` aus dem globalen Admin-Picker scopt Sessions + Memory pro Lern-App.

export function TutorChatsTab({ userId, app, enabled }: Props): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-tutor-sessions', userId, app],
    queryFn: () => fetchUserTutorSessions(userId, app),
    enabled,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div
        className="h-64 animate-pulse rounded-md"
        style={{ background: darkTheme.colors.bgSurface }}
      />
    );
  }
  if (error) {
    return (
      <div
        className="rounded-md p-4 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Fehler beim Laden: {error instanceof Error ? error.message : 'unbekannt'}
      </div>
    );
  }

  const sessions = data?.sessions ?? [];

  return (
    <div className="space-y-4">
      {/* Memory-Einsicht (Spec C5) — eigenständig collapsible, lädt lazy. */}
      <TutorMemorySection userId={userId} app={app} enabled={enabled} />

      {sessions.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          Dieser User hat noch keine Text-Tutor-Gespräche.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div
            className="rounded-md border"
            style={{
              background: darkTheme.colors.bgSurface,
              borderColor: darkTheme.colors.borderSubtle,
            }}
          >
            <div className="border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary"
              style={{ borderColor: darkTheme.colors.borderSubtle }}
            >
              {sessions.length} Gespräch{sessions.length === 1 ? '' : 'e'}
            </div>
            <ul className="max-h-[60vh] overflow-y-auto">
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  active={s.id === selectedId}
                  onSelect={() => setSelectedId(s.id)}
                />
              ))}
            </ul>
          </div>

          <div className="min-w-0">
            {selectedId ? (
              <Transcript userId={userId} sessionId={selectedId} />
            ) : (
              <p className="text-sm text-text-tertiary">
                Wähle links ein Gespräch, um das Transkript zu sehen.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  active,
  onSelect,
}: {
  session: TutorSessionListItem;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left"
        style={{
          borderColor: darkTheme.colors.borderSubtle,
          background: active ? darkTheme.colors.accentSubtle : 'transparent',
        }}
      >
        <span className="truncate text-xs font-medium text-text-primary">
          {session.title?.trim() || 'Neue Unterhaltung'}
        </span>
        <span className="text-[11px] text-text-tertiary">
          {session.messageCount} Nachricht{session.messageCount === 1 ? '' : 'en'}
          {session.lastUserMessageAt ? ` · ${formatRelative(session.lastUserMessageAt)}` : ''}
        </span>
      </button>
    </li>
  );
}

function Transcript({ userId, sessionId }: { userId: string; sessionId: string }): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-tutor-transcript', userId, sessionId],
    queryFn: () => fetchUserTutorSessionTranscript(userId, sessionId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div
        className="h-64 animate-pulse rounded-md"
        style={{ background: darkTheme.colors.bgSurface }}
      />
    );
  }
  if (error || !data) {
    return (
      <div
        className="rounded-md p-4 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Transkript konnte nicht geladen werden.
      </div>
    );
  }

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="truncate text-sm font-semibold text-text-primary">
          {data.title?.trim() || 'Neue Unterhaltung'}
        </h3>
        <span className="shrink-0 text-[11px] text-text-tertiary">
          {data.messages.length} Nachricht{data.messages.length === 1 ? '' : 'en'}
        </span>
      </div>
      <div className="space-y-3">
        {data.messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-xs leading-relaxed"
              style={{
                background:
                  m.role === 'user'
                    ? darkTheme.colors.accentSubtle
                    : darkTheme.colors.bgBase,
                color:
                  m.role === 'user'
                    ? darkTheme.colors.accentLight
                    : darkTheme.colors.textPrimary,
              }}
            >
              <div className="mb-0.5 text-[10px] uppercase tracking-wide opacity-60">
                {m.role === 'user' ? 'User' : 'KI-Dozent'} · {formatDate(m.createdAt)}
              </div>
              {m.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
